/**
 * Google Jobs Validation Service
 * Validates job postings against Google's structured data requirements
 * Provides compliance checking and Rich Results Test integration
 */

import { supabase } from '../lib/supabase';

/**
 * Required fields for Google Jobs compliance
 */
const REQUIRED_FIELDS = [
  'title',
  'description',
  'datePosted',
  'employmentType',
  'hiringOrganization',
  'jobLocation'
];

/**
 * Recommended fields for better visibility
 */
const RECOMMENDED_FIELDS = [
  'baseSalary',
  'validThrough',
  'occupationalCategory',
  'workHours',
  'qualifications',
  'benefits'
];

/**
 * Valid employment types according to Google Jobs
 */
const VALID_EMPLOYMENT_TYPES = [
  'FULL_TIME',
  'PART_TIME',
  'CONTRACTOR',
  'TEMPORARY',
  'INTERN',
  'VOLUNTEER',
  'PER_DIEM',
  'OTHER'
];

/**
 * Validate a job posting against Google Jobs requirements
 * @param {Object} job - Job posting data
 * @returns {Object} Validation result with score and issues
 */
export const validateJobPosting = (job) => {
  const issues = [];
  const warnings = [];
  let score = 0;
  const maxScore = REQUIRED_FIELDS?.length + RECOMMENDED_FIELDS?.length;

  // Check required fields
  REQUIRED_FIELDS?.forEach(field => {
    const value = getFieldValue(job, field);
    if (!value) {
      issues?.push({
        field,
        severity: 'error',
        message: `Required field "${field}" is missing`,
        fix: getFieldFix(field)
      });
    } else {
      score++;
    }
  });

  // Check recommended fields
  RECOMMENDED_FIELDS?.forEach(field => {
    const value = getFieldValue(job, field);
    if (!value) {
      warnings?.push({
        field,
        severity: 'warning',
        message: `Recommended field "${field}" is missing`,
        fix: getFieldFix(field)
      });
    } else {
      score++;
    }
  });

  // Validate employment type
  if (job?.employmentType && !VALID_EMPLOYMENT_TYPES?.includes(job?.employmentType)) {
    issues?.push({
      field: 'employmentType',
      severity: 'error',
      message: `Invalid employment type: "${job?.employmentType}". Must be one of: ${VALID_EMPLOYMENT_TYPES?.join(', ')}`,
      fix: 'Select a valid employment type from the dropdown'
    });
  }

  // Validate title length
  if (job?.title && job?.title?.length < 10) {
    warnings?.push({
      field: 'title',
      severity: 'warning',
      message: 'Job title is too short (minimum 10 characters recommended)',
      fix: 'Provide a more descriptive job title'
    });
  }

  // Validate description length
  const descriptionText = job?.description?.replace(/<[^>]*>/g, '') || '';
  if (descriptionText?.length < 100) {
    issues?.push({
      field: 'description',
      severity: 'error',
      message: 'Job description is too short (minimum 100 characters required)',
      fix: 'Add more details about responsibilities, requirements, and company'
    });
  }

  // Validate dates
  if (job?.validThrough) {
    const validThroughDate = new Date(job.validThrough);
    const now = new Date();
    if (validThroughDate < now) {
      issues?.push({
        field: 'validThrough',
        severity: 'error',
        message: 'Job expiration date is in the past',
        fix: 'Set a future expiration date'
      });
    }
  }

  // Validate salary data
  if (job?.salary?.min && job?.salary?.max) {
    if (job?.salary?.min > job?.salary?.max) {
      issues?.push({
        field: 'baseSalary',
        severity: 'error',
        message: 'Minimum salary cannot be greater than maximum salary',
        fix: 'Correct the salary range'
      });
    }
  }

  // Validate location data
  if (job?.locations && job?.locations?.length === 0) {
    issues?.push({
      field: 'jobLocation',
      severity: 'error',
      message: 'At least one job location is required',
      fix: 'Add at least one location or mark as remote'
    });
  }

  const complianceScore = Math.round((score / maxScore) * 100);
  const isCompliant = issues?.length === 0;

  return {
    isCompliant,
    score: complianceScore,
    issues,
    warnings,
    summary: {
      requiredFieldsComplete: REQUIRED_FIELDS?.length - issues?.filter(i => REQUIRED_FIELDS?.includes(i?.field))?.length,
      requiredFieldsTotal: REQUIRED_FIELDS?.length,
      recommendedFieldsComplete: RECOMMENDED_FIELDS?.filter(f => getFieldValue(job, f))?.length,
      recommendedFieldsTotal: RECOMMENDED_FIELDS?.length,
      totalIssues: issues?.length,
      totalWarnings: warnings?.length
    }
  };
};

/**
 * Get field value from job object (handles nested fields)
 */
const getFieldValue = (job, field) => {
  switch (field) {
    case 'title':
      return job?.title;
    case 'description':
      return job?.description || job?.descriptionHtml;
    case 'datePosted':
      return job?.postedAt || job?.datePosted || job?.created_at;
    case 'employmentType':
      return job?.employmentType;
    case 'hiringOrganization':
      return job?.company?.name;
    case 'jobLocation':
      return job?.locations?.length > 0 ? job?.locations : null;
    case 'baseSalary':
      return job?.salary?.min && job?.salary?.max ? job?.salary : null;
    case 'validThrough':
      return job?.validThrough || job?.locations?.[0]?.validThrough;
    case 'occupationalCategory':
      return job?.occupationalCategory;
    case 'workHours':
      return job?.workSchedule?.length > 0 ? job?.workSchedule : null;
    case 'qualifications':
      return job?.skills?.length > 0 ? job?.skills : null;
    case 'benefits':
      return job?.benefits ? Object.keys(job?.benefits)?.length > 0 : null;
    default:
      return null;
  }
};

/**
 * Get suggested fix for missing field
 */
const getFieldFix = (field) => {
  const fixes = {
    title: 'Add a clear, descriptive job title',
    description: 'Write a detailed job description with responsibilities and requirements',
    datePosted: 'Set the job posting date',
    employmentType: 'Select the employment type (Full-time, Part-time, etc.)',
    hiringOrganization: 'Add company information',
    jobLocation: 'Add at least one job location',
    baseSalary: 'Add salary range for better visibility',
    validThrough: 'Set an expiration date for the job posting',
    occupationalCategory: 'Select the occupational category from Google Jobs Step',
    workHours: 'Specify work schedule (Day, Night, Flexible, etc.)',
    qualifications: 'Add required skills and qualifications',
    benefits: 'List job benefits to attract candidates'
  };
  return fixes?.[field] || `Add ${field} information`;
};

/**
 * Generate JobPosting schema for a job
 * @param {Object} job - Job posting data
 * @returns {Object} Schema.org JobPosting structured data
 */
export const generateJobPostingSchema = (job) => {
  if (!job) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job?.title,
    description: job?.description || job?.descriptionHtml?.replace(/<[^>]*>/g, ''),
    datePosted: job?.postedAt || job?.datePosted || job?.created_at,
    employmentType: job?.employmentType
  };

  // Add hiring organization
  if (job?.company) {
    schema.hiringOrganization = {
      '@type': 'Organization',
      name: job?.company?.name,
      sameAs: job?.company?.websiteUrl,
      logo: job?.company?.logoUrl
    };
  }

  // Add job locations
  if (job?.locations?.length > 0) {
    schema.jobLocation = job?.locations?.map(location => ({
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: location?.address?.split(', ')?.[0] || '',
        addressRegion: location?.address?.split(', ')?.[1] || '',
        addressCountry: 'US'
      }
    }));
  }

  // Add salary information
  if (job?.salary?.min && job?.salary?.max) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: job?.salary?.currency || 'USD',
      value: {
        '@type': 'QuantitativeValue',
        minValue: parseInt(job?.salary?.min),
        maxValue: parseInt(job?.salary?.max),
        unitText: job?.salary?.unit?.toUpperCase() || 'YEAR'
      }
    };
  }

  // Add optional fields
  if (job?.validThrough || job?.locations?.[0]?.validThrough) {
    schema.validThrough = job?.validThrough || job?.locations?.[0]?.validThrough;
  }

  if (job?.occupationalCategory) {
    schema.occupationalCategory = job?.occupationalCategory;
  }

  if (job?.workSchedule?.length > 0) {
    schema.workHours = job?.workSchedule?.map(ws => ws?.replace('_', ' ')?.toLowerCase())?.join(', ');
  }

  if (job?.skills?.length > 0) {
    schema.qualifications = job?.skills?.join(', ');
  }

  if (job?.benefits) {
    const benefitsList = Object.entries(job?.benefits)?.filter(([key, value]) => value === true && key !== 'customBenefits')?.map(([key]) => key?.replace(/([A-Z])/g, ' $1')?.toLowerCase()?.replace(/^\w/, c => c?.toUpperCase()))?.concat(job?.benefits?.customBenefits || []);
    
    if (benefitsList?.length > 0) {
      schema.benefits = benefitsList?.join(', ');
    }
  }

  if (job?.applicationDetails?.applicationEmail || job?.applicationDetails?.applicationUrl) {
    schema.applicationContact = {
      '@type': 'ContactPoint',
      email: job?.applicationDetails?.applicationEmail,
      url: job?.applicationDetails?.applicationUrl
    };
  }

  return schema;
};

/**
 * Get Rich Results Test URL for a job
 * @param {string} jobId - Job ID
 * @returns {string} Google Rich Results Test URL
 */
export const getRichResultsTestUrl = (jobId) => {
  const jobUrl = `${window.location?.origin}/job-details/${jobId}`;
  return `https://search.google.com/test/rich-results?url=${encodeURIComponent(jobUrl)}`;
};

/**
 * Get Search Console URL for job indexing status
 * @returns {string} Google Search Console URL
 */
export const getSearchConsoleUrl = () => {
  const domain = window.location?.hostname;
  return `https://search.google.com/search-console?resource_id=sc-domain:${domain}`;
};

/**
 * Validate all jobs for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} Validation results for all jobs
 */
export const validateAllCompanyJobs = async (companyId) => {
  try {
    const { data: jobs, error } = await supabase?.from('jobs')?.select(`
        *,
        company:companies(*),
        locations:job_locations(*)
      `)?.eq('company_id', companyId)?.eq('status', 'ACTIVE');

    if (error) throw error;

    return jobs?.map(job => ({
      jobId: job?.id,
      title: job?.title,
      validation: validateJobPosting(job),
      richResultsUrl: getRichResultsTestUrl(job?.id)
    }));
  } catch (error) {
    console.error('Error validating company jobs:', error);
    throw error;
  }
};

/**
 * Get validation statistics for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Validation statistics
 */
export const getCompanyValidationStats = async (companyId) => {
  try {
    const results = await validateAllCompanyJobs(companyId);
    
    const stats = {
      totalJobs: results?.length,
      compliantJobs: results?.filter(r => r?.validation?.isCompliant)?.length,
      averageScore: Math.round(
        results?.reduce((sum, r) => sum + r?.validation?.score, 0) / results?.length
      ),
      totalIssues: results?.reduce((sum, r) => sum + r?.validation?.issues?.length, 0),
      totalWarnings: results?.reduce((sum, r) => sum + r?.validation?.warnings?.length, 0),
      jobsNeedingAttention: results?.filter(r => !r?.validation?.isCompliant)?.length
    };

    return stats;
  } catch (error) {
    console.error('Error getting validation stats:', error);
    throw error;
  }
};

export default {
  validateJobPosting,
  generateJobPostingSchema,
  getRichResultsTestUrl,
  getSearchConsoleUrl,
  validateAllCompanyJobs,
  getCompanyValidationStats
};